import { lazy, Suspense } from 'react'
import { Routes, Route, Outlet } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import Layout from './components/Layout'
import { LanguageLayout, LanguageRedirect, CatchAllRedirect } from './components/LanguageRedirect'
import LandingPage from './pages/Landing/LandingPage'
import Home from './pages/Publisher/Home'
import CreateForm from './pages/Publisher/CreateForm'
import Dashboard from './pages/Publisher/Dashboard'
import SignForm from './pages/Signer/SignForm'
import Login from './pages/Auth/Login'
import Account from './pages/Auth/Account'
import Pricing from './pages/Pricing/Pricing'
import PaymentSuccess from './pages/Payment/PaymentSuccess'
import PaymentCancel from './pages/Payment/PaymentCancel'
import LegalPage from './pages/Legal/LegalPage'

// Admin pages (lazy-loaded)
const AdminLogin = lazy(() => import('./pages/Admin/AdminLogin'))
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'))
const AdminCustomers = lazy(() => import('./pages/Admin/AdminCustomers'))
const AdminCustomerDetail = lazy(() => import('./pages/Admin/AdminCustomerDetail'))
const AdminLayout = lazy(() => import('./components/AdminLayout'))

function AdminWrapper() {
  return (
    <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}>
      <AdminLayout>
        <Outlet />
      </AdminLayout>
    </Suspense>
  )
}

function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Root: redirect to detected language */}
        <Route path="/" element={<LanguageRedirect />} />

        {/* Language-prefixed routes */}
        <Route path="/:lang" element={<LanguageLayout />}>
          {/* Landing page — own layout (no Layout wrapper) */}
          <Route index element={<LandingPage />} />

          {/* App routes — wrapped in Layout */}
          <Route element={<Layout><Outlet /></Layout>}>
            <Route path="access" element={<Home />} />
            <Route path="create" element={<CreateForm />} />
            <Route path="dashboard/:formId" element={<Dashboard />} />
            <Route path="sign/:formId" element={<SignForm />} />
            <Route path="login" element={<Login />} />
            <Route path="account" element={<Account />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="payment/success" element={<PaymentSuccess />} />
            <Route path="payment/cancel" element={<PaymentCancel />} />
            <Route path="legal/:type" element={<LegalPage />} />
          </Route>
        </Route>

        {/* Admin routes (lazy-loaded, own layout) */}
        <Route path="/admin/login" element={
          <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}>
            <AdminLogin />
          </Suspense>
        } />
        <Route path="/admin" element={<AdminWrapper />}>
          <Route index element={
            <Suspense fallback={null}><AdminDashboard /></Suspense>
          } />
          <Route path="customers" element={
            <Suspense fallback={null}><AdminCustomers /></Suspense>
          } />
          <Route path="customers/:userId" element={
            <Suspense fallback={null}><AdminCustomerDetail /></Suspense>
          } />
        </Route>

        {/* Catch-all: redirect old paths without lang prefix */}
        <Route path="*" element={<CatchAllRedirect />} />
      </Routes>
    </AuthProvider>
  )
}

export default App
